if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
      });
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
    }

    connectedCallback() {
      if (!this.elements.viewer || this.videoObserver) return;

      this.videoVisibility = new Map();
      this.videoItems = new Map();
      this.activeVideoMedia = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onVideoVisibilityChange = this.updateVisibleVideo.bind(this);
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (this.videoVisibility.has(entry.target)) this.videoVisibility.set(entry.target, entry);
        });
        this.updateVisibleVideo();
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] });

      this.videoMutationObserver = new MutationObserver(() => this.syncVideoMedia());
      this.videoMutationObserver.observe(this.elements.viewer, { childList: true, subtree: true });
      this.reducedMotion.addEventListener('change', this.onVideoVisibilityChange);
      document.addEventListener('visibilitychange', this.onVideoVisibilityChange);
      this.syncVideoMedia();
    }

    disconnectedCallback() {
      this.videoObserver?.disconnect();
      this.videoMutationObserver?.disconnect();
      this.reducedMotion?.removeEventListener('change', this.onVideoVisibilityChange);
      document.removeEventListener('visibilitychange', this.onVideoVisibilityChange);
      this.videoVisibility?.forEach((entry, media) => media.querySelector('video')?.pause());
      this.videoVisibility?.clear();
      this.videoItems?.clear();
      this.videoObserver = null;
      this.activeVideoMedia = null;
    }

    syncVideoMedia() {
      const mediaElements = new Set(Array.from(this.elements.viewer.querySelectorAll('deferred-media')).filter((media) => {
        return media.querySelector('video') || media.querySelector('template')?.content.querySelector('video');
      }));

      this.videoVisibility.forEach((entry, media) => {
        if (mediaElements.has(media)) return;
        this.videoObserver.unobserve(media);
        media.querySelector('video')?.pause();
        this.videoItems.get(media)?.classList.remove('product__media-item--autoplay-video');
        this.videoItems.delete(media);
        this.videoVisibility.delete(media);
        if (this.activeVideoMedia === media) this.activeVideoMedia = null;
      });
      mediaElements.forEach((media) => {
        if (this.videoVisibility.has(media)) return;
        const item = media.closest('.product__media-item');
        item?.classList.add('product__media-item--autoplay-video');
        this.videoItems.set(media, item);
        this.videoVisibility.set(media, null);
        this.videoObserver.observe(media);
      });
    }

    updateVisibleVideo() {
      let activeMedia = null;
      let largestRatio = 0;
      if (!document.hidden && !this.reducedMotion.matches) {
        this.videoVisibility.forEach((entry, media) => {
          if (!entry?.isIntersecting || entry.intersectionRatio < 0.25 || entry.intersectionRatio <= largestRatio) return;
          activeMedia = media;
          largestRatio = entry.intersectionRatio;
        });
      }
      if (activeMedia === this.activeVideoMedia) return;

      this.activeVideoMedia = activeMedia;
      this.videoVisibility.forEach((entry, media) => {
        if (media !== activeMedia) media.querySelector('video')?.pause();
      });
      if (!activeMedia) return;

      let video = activeMedia.querySelector('video');
      if (!video) {
        video = activeMedia.querySelector('template')?.content.querySelector('video')?.cloneNode(true);
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.controls = true;
        activeMedia.appendChild(video);
        activeMedia.setAttribute('loaded', 'true');
      }
      video.muted = true;
      video.playsInline = true;
      const playback = video.play();
      if (playback?.catch) playback.catch(() => {});
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (this.videoVisibility?.has(deferredMedia)) {
        this.updateVisibleVideo();
        return;
      }
      window.pauseAllMedia();
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
